/**
 * @file     blogIndexQuery.ts
 * @purpose  Pure helpers for the /blog index query string (category filter and page), shared by the server loader and the client filter links.
 */
import type { BlogCategory } from '@/features/blog/types/LocalBlogPost';

export const BLOG_PAGE_SIZE = 24;

export const BLOG_CATEGORIES: readonly BlogCategory[] = [
  'market-update',
  'buyer-guide',
  'seller-guide',
  'presale',
  'neighbourhood',
];

export type BlogIndexQuery = { category: BlogCategory | 'all'; page: number };

export const isBlogCategory = (value: unknown): value is BlogCategory =>
  typeof value === 'string' && (BLOG_CATEGORIES as readonly string[]).includes(value);

const first = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

export function parseBlogIndexQuery(searchParams: Record<string, string | string[] | undefined>): BlogIndexQuery {
  const category = first(searchParams.category);
  const page = Number.parseInt(first(searchParams.page) ?? '', 10);
  return {
    category: isBlogCategory(category) ? category : 'all',
    page: Number.isFinite(page) && page > 1 ? Math.min(page, 500) : 1,
  };
}

/** Builds the index URL for a category and page, dropping defaults so /blog stays canonical. */
export function blogIndexHref(category: BlogCategory | 'all', page = 1): string {
  const qs = new URLSearchParams();
  if (category !== 'all') qs.set('category', category);
  if (page > 1) qs.set('page', String(page));
  const s = qs.toString();
  return s ? `/blog?${s}` : '/blog';
}
